import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Animated SVG mascot "Dubi" — friendly, professional, modern.
 * Uses brand gradient (green → blue → purple). Waves on mount, then idles.
 * Includes a glass speech bubble. Designed to be placed in the homepage hero.
 */
export default function DubiMascot({ className = '', onChatOpen, speech }) {
  const [bubbleVisible, setBubbleVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setBubbleVisible(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`relative inline-block ${className}`} data-testid="dubi-hero-mascot">
      {/* Speech bubble */}
      {bubbleVisible && speech && (
        <div className="absolute -top-2 -left-2 md:-left-44 lg:-left-48 max-w-[220px] glass rounded-2xl px-4 py-3 text-sm text-slate-800 shadow-xl dubi-bubble-anim">
          <p className="font-semibold leading-snug">{speech}</p>
          <button
            data-testid="dubi-hero-cta"
            onClick={onChatOpen}
            className="mt-2 text-xs font-bold text-blue-700 hover:underline"
          >
            Razgovaraj sa mnom →
          </button>
          <span className="absolute -bottom-2 right-6 w-4 h-4 rotate-45 bg-white/70 border-r border-b border-white/60" />
        </div>
      )}

      <button
        type="button"
        aria-label="Dubi — pričaj sa mnom"
        onClick={onChatOpen}
        className="dubi-float block focus:outline-none"
        data-testid="dubi-hero-character"
      >
        <DubiSvg className="w-40 h-44 md:w-52 md:h-60 drop-shadow-2xl" />
      </button>

      {/* Floating sparkles */}
      <span className="absolute -left-6 top-2 w-2 h-2 rounded-full bg-emerald-400 sparkle-1" />
      <span className="absolute -right-4 top-12 w-1.5 h-1.5 rounded-full bg-purple-400 sparkle-2" />
      <span className="absolute -left-3 bottom-12 w-2 h-2 rounded-full bg-blue-400 sparkle-3" />
    </div>
  );
}

export function DubiSvg({ className = '' }) {
  return (
    <svg viewBox="0 0 220 260" xmlns="http://www.w3.org/2000/svg" className={className}
      role="img" aria-label="Dubi maskota">
      <defs>
        <linearGradient id="dubiBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="55%" stopColor="#0466C8" />
          <stop offset="100%" stopColor="#5B21B6" />
        </linearGradient>
        <linearGradient id="dubiArm" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#0466C8" />
        </linearGradient>
        <radialGradient id="dubiCheek" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F472B6" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#F472B6" stopOpacity="0" />
        </radialGradient>
        <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
        </filter>
      </defs>

      {/* Ground shadow */}
      <ellipse cx="110" cy="240" rx="62" ry="8" fill="rgba(2,62,138,0.15)" />

      {/* Body (rounded square) */}
      <rect x="40" y="70" width="140" height="155" rx="56" fill="url(#dubiBody)" />

      {/* Inner gloss */}
      <path d="M 60 90 Q 110 80 160 95 L 158 110 Q 110 100 62 110 Z" fill="white" opacity="0.18" />

      {/* Chest sparkle badge */}
      <g transform="translate(110,170)">
        <circle r="18" fill="rgba(255,255,255,0.18)" />
        <path d="M 0 -10 L 3 -3 L 10 0 L 3 3 L 0 10 L -3 3 L -10 0 L -3 -3 Z" fill="white" />
      </g>

      {/* Cheeks */}
      <circle cx="70" cy="138" r="11" fill="url(#dubiCheek)" />
      <circle cx="150" cy="138" r="11" fill="url(#dubiCheek)" />

      {/* Eyes */}
      <g>
        <circle cx="86" cy="118" r="8" fill="white" />
        <circle cx="134" cy="118" r="8" fill="white" />
        <circle cx="88" cy="120" r="3.2" fill="#0F172A" />
        <circle cx="136" cy="120" r="3.2" fill="#0F172A" />
        <circle cx="89.5" cy="118.5" r="1.1" fill="white" />
        <circle cx="137.5" cy="118.5" r="1.1" fill="white" />
      </g>

      {/* Smile */}
      <path d="M 88 145 Q 110 162 132 145" stroke="white" strokeWidth="3.2" strokeLinecap="round" fill="none" />

      {/* Antenna with sparkle on top */}
      <line x1="110" y1="70" x2="110" y2="42" stroke="white" strokeWidth="3" strokeLinecap="round" />
      <g transform="translate(110,38)" className="dubi-antenna-spark">
        <circle r="6" fill="#FCD34D" />
        <circle r="2.4" fill="white" />
      </g>

      {/* Left arm (static, holding tiny spray) */}
      <g>
        <rect x="22" y="120" width="20" height="48" rx="10" fill="url(#dubiArm)" />
        <circle cx="32" cy="173" r="11" fill="white" />
        {/* spray bottle */}
        <rect x="22" y="178" width="22" height="20" rx="4" fill="#10B981" />
        <rect x="29" y="170" width="8" height="10" rx="2" fill="#10B981" />
        <circle cx="36" cy="174" r="2" fill="#0F172A" opacity="0.4" />
      </g>

      {/* Right arm (waving) */}
      <g className="dubi-arm" style={{ transformOrigin: '178px 118px' }}>
        <rect x="170" y="112" width="20" height="48" rx="10" fill="url(#dubiArm)" />
        <circle cx="180" cy="166" r="11" fill="white" />
        {/* Waving sparkle */}
        <g transform="translate(195,158)">
          <path d="M 0 -7 L 2 -2 L 7 0 L 2 2 L 0 7 L -2 2 L -7 0 L -2 -2 Z" fill="#FCD34D" />
        </g>
      </g>

      {/* Tiny feet */}
      <ellipse cx="86" cy="224" rx="14" ry="6" fill="#0F172A" opacity="0.7" />
      <ellipse cx="134" cy="224" rx="14" ry="6" fill="#0F172A" opacity="0.7" />
    </svg>
  );
}
